import {
  put,
  call,
  select,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';

import {
  FINALIZE_REQUEST,
  QUEUE_REQUEST,
  hasActiveRequestsSelector,
} from 'redux-flight-control';

import { clearGlobal, setGlobal } from 'utils/global';
import { xmlToJs } from 'utils/xml';

import {
  redirectToLogin,
  redirectToLogout,
} from '../api';

import {
  REFRESH_AUTH_TOKEN,
  GLOBAL_ACCESS_TOKEN_KEY,
  TICKET_VALIDATION_FAILURE,
  VALIDATE_SSO_TICKET,
} from '../constants';

import {
  decodeAuthToken,
  refreshAuthToken,
  loginSuccess,
  ticketValidationFailure,
} from './actions';

import {
  isTokenAlmostExpiredSelector,
  isTokenExpiredSelector,
  decodeAuthTokenErrorSelector,
  currentTenantSelector,
} from './selectors';

/**
 * Handles saving and decoding of a new auth token.
 * @param {string} authToken A valid JWT
 */
function* processNewAuthToken(authToken) {
  // store the authToken as an application global for use by API requests:
  yield call(setGlobal, GLOBAL_ACCESS_TOKEN_KEY, authToken);

  // dispatch the API request to get the decoded version of the JWT
  yield put(decodeAuthToken());
}

/* ------------------------------- worker sagas ------------------------------- */

/**
 * This worker function handles the results of the Portunus API call which
 * validates the ticket-granting ticket.
 *
 * If the ticket is valid, the payload will contain an XML document with
 * `authenticationSuccess`, including details on the user, auth token, etc.
 * This data will be stored in the redux store.
 *
 * If the ticket is NOT valid, the payload will contain an `authenticationFailure`
 * with a code and text indicating the reason for failure.
 *
 * @param payload The ticket-validation API response payload.
 */
function* validateSSOWorker({ payload }) {
  const xml = yield call(xmlToJs, payload.body);
  const serviceResponse = xml.serviceResponse;
  const authSuccess = serviceResponse.authenticationSuccess;

  if (authSuccess) { // 👍
    const user = authSuccess.user;
    const attributes = authSuccess.attributes;
    const authToken = attributes.auth_token;
    const authedAt = attributes.authenticationDate;
    const email = attributes.email;
    const isFromNewLogin = attributes.isFromNewLogin;
    const userID = attributes.user_id;
    const uuid = attributes.uuid;

    yield call(processNewAuthToken, authToken);

    // send our auth data to the Redux store:
    yield put(loginSuccess({
      user,
      authToken,
      authedAt,
      email,
      isFromNewLogin,
      userID,
      uuid,
    }));
  } else { // 👎
    // clear the global authToken
    yield call(clearGlobal, GLOBAL_ACCESS_TOKEN_KEY);
    const authFailure = serviceResponse.authenticationFailure;
    if (authFailure) {
      const authFailureAttrs = authFailure.$;
      const failureCode = authFailureAttrs.code;
      const failureText = authFailure._;
      console.error(
        'FAILED SSO VALIDATION: failureCode=', failureCode,
        'error text=', failureText,
      );
      yield put(ticketValidationFailure({
        message: failureText,
        code: failureCode,
      }));
    }
  }
}

/**
 * This worker takes care of the response to a failed Portunus ticket validation.
 */
function* ticketValidationFailureWorker() {
  const tenant = yield select(currentTenantSelector);

  yield call(redirectToLogin, tenant);
}

/**
 * This worker logs the user out if the token is within one minute of expiry.
 * A one minute buffer was selected to create enough buffer to process active
 * requests and refresh the token.
 */
function* requestQueueWorker() {
  // - if token is expired, redirect to logout
  const isTokenExpired = yield select(isTokenExpiredSelector);
  if (isTokenExpired) {
    yield call(redirectToLogout);
  }
}

/**
 * This worker monitors the requests queue, and under certain conditions logs the user out or
 * dispatches an action that refreshes the auth token.
 * @param {object} action A finalize request action, dispatched by redux request action system
 */
function* requestFinalizeWorker({
  meta: {
    requestType,
  },
}) {
  // - if the decode auth token request fails, the most likely cause is the token is invalid.
  //   Log the user out to get a new one.
  const decodeAuthTokenError = yield select(decodeAuthTokenErrorSelector);
  if (decodeAuthTokenError) {
    yield call(redirectToLogout);
  }

  // - if token is almost expired (within 15 minutes of expiry), there are NO active requests,
  //   and this isn't finalize a refresh auth token request, refresh the auth token.
  const isTokenAlmostExpired = yield select(isTokenAlmostExpiredSelector);
  const hasActiveRequests = yield select(hasActiveRequestsSelector);
  if (
    !hasActiveRequests &&
    isTokenAlmostExpired &&
    requestType !== REFRESH_AUTH_TOKEN
  ) {
    yield put(refreshAuthToken());
  }
}

/**
 * This worker watches for a refresh auth token action, dispatched only on success.
 * It extracts and begins processing the new authToken.
 * @param {object} action A successful refresh token action
 */
function* refreshAuthTokenWorker({
  payload: {
    body: {
      data: {
        attributes: {
          access_token: authToken,
        },
      },
    },
  },
}) {
  yield call(processNewAuthToken, authToken);
}

/* ------------------------------- watcher sagas ------------------------------- */

export function* validateSSOWatcher() {
  yield takeLatest(VALIDATE_SSO_TICKET, validateSSOWorker);
}

export function* ticketValidationFailureWatcher() {
  yield takeLatest(TICKET_VALIDATION_FAILURE, ticketValidationFailureWorker);
}

export function* requestQueueWatcher() {
  yield takeEvery(QUEUE_REQUEST, requestQueueWorker);
}

export function* requestFinalizeWatcher() {
  yield takeLatest(FINALIZE_REQUEST, requestFinalizeWorker);
}

export function* refreshAuthTokenWatcher() {
  yield takeLatest(REFRESH_AUTH_TOKEN, refreshAuthTokenWorker);
}

// export all of the watcher sagas.
export default [
  refreshAuthTokenWatcher(),
  requestFinalizeWatcher(),
  requestQueueWatcher(),
  ticketValidationFailureWatcher(),
  validateSSOWatcher(),
];



// WEBPACK FOOTER //
// ./features/authentication/redux/sagas.js